/**
 * Page des annonces (`/annonces`) : quel onglet par défaut, quelles annonces garder, dans quel ordre.
 * Logique pure, testée dans utils/__tests__/annoncesFilters.test.js ; l'écran (`AnnoncesPage`) affiche.
 *
 * Une annonce est l'objet normalisé de la page : { type: 'trajet' | 'colis', from, to, price, weight, user, raw }.
 */

/** Onglet par défaut selon le rôle : l'expéditeur cherche un transporteur, le transporteur un colis ; sinon tout. */
export function defaultFilterType(user) {
  const roles = user?.roles || [];
  const sender = roles.includes('ROLE_SENDER');
  const carrier = roles.includes('ROLE_CARRIER');
  if (sender && !carrier) return 'trajet';
  if (carrier && !sender) return 'colis';
  return 'all';
}

// Seuls les transporteurs s'abonnent : la mise en avant ne concerne que les trajets
export const isProTrip = (a) => a.type === 'trajet' && !!a.user?.subscribed;

// « Recommandés » (tri par défaut) : trajets des transporteurs abonnés PRO en tête, puis les plus récents.
// Les tris choisis explicitement (date, prix, score) restent neutres.
export function compareAnnonces(sortBy, a, b) {
  const recent = () => new Date(b.raw.created_at || 0) - new Date(a.raw.created_at || 0);
  if (sortBy === 'recommended') return (Number(isProTrip(b)) - Number(isProTrip(a))) || recent();
  if (sortBy === 'recent') return recent();
  if (sortBy === 'date-asc') return new Date(a.raw.departure_date) - new Date(b.raw.departure_date);
  if (sortBy === 'price-asc') return a.price - b.price;
  if (sortBy === 'price-desc') return b.price - a.price;
  if (sortBy === 'score-desc') return (parseFloat(String(b.user.score).replace(',', '.')) || 0) - (parseFloat(String(a.user.score).replace(',', '.')) || 0);
  return 0;
}

/** Libellé court du tri, pour la phrase des résultats (« 9 transporteurs · les plus proches du départ d'abord »). */
export const SORT_LABELS = {
  recommended: 'recommandés d\'abord',
  recent: 'les plus récents d\'abord',
  'date-asc': 'les plus proches du départ d\'abord',
  'price-asc': 'les moins chers d\'abord',
  'price-desc': 'les plus chers d\'abord',
  'score-desc': 'les mieux notés d\'abord',
};

/** Devises acceptées par l'annonce (sa devise principale comprise) — décision de l'utilisateur du 2026-10-29. */
export function acceptedCurrencies(raw) {
  const list = Array.isArray(raw?.currencies) ? raw.currencies : [];
  return [...new Set([raw?.primary_currency || 'EUR', ...list])];
}

/** Prix de l'annonce converti en euros pour le curseur de prix (jamais affiché : les cartes gardent leur devise). */
function priceInEur(a, rates) {
  const ccy = a.raw?.primary_currency || 'EUR';
  const rate = rates?.[ccy] ?? 1;
  const mode = a.raw?.pricing_mode;
  const amount = a.type === 'colis'
    ? Number(a.raw?.price_offered ?? 0)
    : mode === 'kg' ? Number(a.raw?.price_per_kg ?? 0)
      : mode === 'fixe' ? Number(a.raw?.price_fixed ?? 0)
        : 0;
  return Number.isNaN(amount) || amount <= 0 ? 0 : amount / rate;
}

/**
 * L'annonce passe-t-elle les filtres ?
 * - from / to : la ville contient le texte saisi
 * - beforeDate ('AAAA-MM-JJ') : date de départ du trajet, ou date limite du colis, au plus tard ce jour-là
 * - weightKg : côté trajet, au moins ce poids de place libre (« Mon colis pèse ») ; côté colis, au plus ce poids
 *   (« Ma place libre ») — avant le 2026-10-29, un « poids max » gardait à tort les trajets qui avaient MOINS de place
 * - currencies : l'annonce accepte au moins une de ces devises (principale ou acceptée) ; aucune conversion
 * - maxPriceEur (null : pas de plafond) avec rates : prix converti en euros au plus égal ; un prix nul passe
 * - verified, entreprise, subscribed : profil vérifié, entreprise validée, transporteur abonné PRO
 * - modes : le trajet utilise au moins un de ces moyens de transport (un colis n'en a pas : il passe)
 */
export function matchesFilters(a, f = {}) {
  const has = (city, q) => !q || (city || '').toLowerCase().includes(q.trim().toLowerCase());
  if (!has(a.from, f.from) || !has(a.to, f.to)) return false;
  if (f.beforeDate && a.raw?.departure_date && String(a.raw.departure_date).slice(0, 10) > f.beforeDate) return false;
  const kg = Number(f.weightKg);
  if (kg > 0 && a.weight != null) {
    if (a.type === 'trajet' && Number(a.weight) < kg) return false;
    if (a.type === 'colis' && Number(a.weight) > kg) return false;
  }
  if (f.currencies?.length && !acceptedCurrencies(a.raw).some((c) => f.currencies.includes(c))) return false;
  if (f.maxPriceEur != null) {
    const eur = priceInEur(a, f.rates);
    if (eur > 0 && eur > f.maxPriceEur) return false;
  }
  if (f.verified && !a.user?.verified) return false;
  if (f.entreprise && !a.user?.pro) return false;
  if (f.subscribed && !isProTrip(a)) return false;
  if (f.modes?.length && a.type === 'trajet') {
    const modes = a.raw?.transport_modes || [];
    if (!f.modes.some((m) => modes.includes(m))) return false;
  }
  return true;
}
